import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoaderComponent from '@components/LoaderComponent';

import useWindowSize from '@hooks/useWindowSize';

import SuggestedCollection from '@features/collection/SuggestedCollection';
import FilterTopHeader from '@features/filter/FilterTopHeader';
import {
	allFilterPages,
	collectionFieldList,
	generateCollectionListParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import BuyerRequestCollectionItem from '@features/collection/components/BuyerRequestCollectionItem';
import CollectionItem from '@features/collection/components/CollectionItem';

import { clearFilterResponse, handleFilter, setHasMentionedId } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDependentFilterList } from '@services/commonService';

import { isPageReachBottom } from '@utils/Util';

const CollectionListV2 = ({ onClickTab, onSetDefaultView, pathName, selectedTab }) => {
	const [loader] = useState(false);
	const mentionedData = useSelector((store) => store.chatReducer.mentionedData);
	const filterData = useSelector((store) => store.filterFieldStore);
	const filterResponse = useSelector((store) => store.filterResponseStore);
	const [showFilter, setShowFilter] = useState(false);
	const [totalSuggestedCollection, setTotalSuggestedCollection] = useState(0);
	const [activeTab, setActiveTab] = useState('Development');
	let filterfields = useSelector((store) => store.filterFieldStore);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const [page, setPage] = useState(0);
	const { ...paramsData } = generateCollectionListParams(filterfields);
	const dispatch = useDispatch();
	const activeTabRef = useRef('Development');
	const scrollRef = useRef({});

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useWindowSize(() => setShowFilter(false));

	useEffect(() => {
		if (mentionedData?.collectionId) {
			dispatch(setHasMentionedId(mentionedData?.collectionId));
		}
	}, [mentionedData]);

	useEffect(() => {
		setCurrentState(filterResponse);
	}, [filterResponse]);

	const removeAvailabilityStatusAll = (data) => {
		if (Array.isArray(data.availabilityStatusList)) {
			const index = data.availabilityStatusList.indexOf('ALL');
			if (index > -1) {
				data.availabilityStatusList.splice(index, 1);
			}

			if (data.availabilityStatusList.length === 0) {
				if (pathName === '/nitex-studio') {
					data.availabilityStatusList = ['PUBLISHED'];
				} else {
					delete data.availabilityStatusList;
				}
			}
		}

		return data;
	};

	const getFilterOptions = () => {
		const cleanParamsData = removeAvailabilityStatusAll({ ...paramsData });
		dispatch(fetchDependentFilterList({ type: 'COLLECTION_LIST' }, cleanParamsData));
	};

	useEffect(() => {
		if (showFilter) {
			getFilterOptions();
		}
	}, [filterData, showFilter]);

	const fetchCollectionList = () => {
		window.scrollTo(0, 0);
		dispatch(openLoader());
		setPage(0);
		let queryParams = generateCollectionListParams(filterfields, 0, sortValue);
		queryParams = removeAvailabilityStatusAll(queryParams);
		dispatch(handleFilter('getCollectionList', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	};

	useEffect(() => {
		if (filterData.availabilityStatusList) {
			fetchCollectionList();
		}
	}, [filterData]);

	useEffect(() => {
		if (page !== 0) {
			dispatch(openLoader());
			let queryParams = generateCollectionListParams(filterfields, page, sortValue);
			queryParams = removeAvailabilityStatusAll(queryParams);
			dispatch(handleFilter('getCollectionList', queryParams, true)).finally(() => {
				dispatch(closeLoader());
			});
		}
	}, [page]);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			dispatch(clearFilterResponse());
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	useEffect(() => {
		let activeTab = filterfields?.availabilityStatusList || [];
		let currentTabOfGlobal = activeTab.find((item) => item.type === 'SELECT');
		setActiveTab(currentTabOfGlobal?.key);
		activeTabRef.current = currentTabOfGlobal?.key;
	}, [filterfields]);

	const scrollPosition = sessionStorage.getItem('scrollPosition') || 0;

	useEffect(() => {
		window.scrollTo(0, scrollPosition);
	}, [scrollPosition]);

	const renderCollectionList = () => {
		if (filterResponse?.data?.length > 0) {
			return filterResponse.data.map((collection, index) => {
				let Component;
				let imageType;

				if (collection?.moodboardImageList?.length > 0) {
					Component = BuyerRequestCollectionItem;
					imageType = 'MOODBOARD_IAMGE';
				} else if (collection?.moodboardProductImageList?.length > 0) {
					Component =
						collection?.moodboardProductImageList?.length > 3
							? BuyerRequestCollectionItem
							: CollectionItem;
					imageType = 'PRODUCT_IAMGE';
				} else {
					Component = CollectionItem;
				}

				return (
					<Component
						collection={collection}
						index={index}
						key={`collection_filter_7_${index}`}
						collectionType={activeTab}
						pathName={pathName}
						imageType={imageType}
						fetchCollectionList={fetchCollectionList}
					/>
				);
			});
		}
	};

	const getCollectionFieldList = (collectionFieldList) => {
		if (pathName === '/nitex-studio') {
			const { ...rest } = collectionFieldList;
			return rest;
		}
		return collectionFieldList;
	};

	const renderAlertMessage = () => {
		if (filterResponse?.data?.length === 0) {
			return (
				<div className="alert alert-secondary text-center">
					<p>No collection found</p>
				</div>
			);
		}
	};

	return (
		<LoaderComponent loading={loader}>
			<FilterTopHeader
				setShowFilter={setShowFilter}
				showFilter={showFilter}
				addNewTitle="Collection"
				searchBoxPlaceHolder={'Collection name'}
				onClickTab={onClickTab}
				onSetDefaultView={onSetDefaultView}
				isFromNitexStudio={pathName === '/nitex-studio'}
				viewType={selectedTab}
				totalElements={activeTab === 'SUGGESTED_FOR_BUYER' ? totalSuggestedCollection : null}
				isShowArchiveTab={false}
			/>
			<section
				className={`filter-data-container ${
					pathName === '/nitex-studio' ? 'nitex-studio-collection' : ''
				} filter-v3-data-container under-inner-tabs collection-list-data-container bg-white`}
			>
				<FilterComponent
					filterPageName={allFilterPages.COLLECTION_LIST}
					fields={getCollectionFieldList(collectionFieldList)}
					showFilter={showFilter}
					isDependentFilter={true}
				/>

				<div className="data-table">
					{activeTab !== 'SUGGESTED_FOR_BUYER' && (
						<div className="designs-collections-container">
							<div className="collections-row">{renderCollectionList()}</div>
							{renderAlertMessage()}
						</div>
					)}
					{activeTab === 'SUGGESTED_FOR_BUYER' && (
						<SuggestedCollection setTotalCount={setTotalSuggestedCollection} />
					)}
				</div>
			</section>
		</LoaderComponent>
	);
};

export default CollectionListV2;
